import { Bell } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useGetNotificationUnreadCount } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import {
  pickBackgroundPollInterval,
  useVisibilityState,
} from "@/lib/polling";
import { useAuth } from "@/lib/auth-context";

export function NotificationsBell({ variant = "icon" }: { variant?: "icon" | "menu" }) {
  const { t } = useTranslation("app");
  const [location] = useLocation();
  const visibility = useVisibilityState();
  const { user } = useAuth();

  const unread = useGetNotificationUnreadCount({
    query: {
      queryKey: ["/api/notifications/unread-count", user?.id],
      enabled: !!user,
      refetchInterval: pickBackgroundPollInterval(30_000, visibility),
    } as never,
  });
  const count = unread.data?.count ?? 0;
  const active = location === "/notifications";

  return (
    <Link href="/notifications" asChild>
      <a
          aria-label={t("notifications.title")}
          data-testid="button-notifications"
          className={cn(
            "relative flex items-center transition-colors hover:text-[#FF8A4B]",
            variant === "icon" ? "justify-center" : "nav-row w-full gap-[11px]",
          )}
          style={{
            width: variant === "icon" ? 40 : "100%",
            height: variant === "icon" ? 40 : "auto",
            padding: variant === "menu" ? "10px 12px" : undefined,
            borderRadius: variant === "icon" ? 13 : 11,
            border: variant === "icon" ? "1px solid rgba(255,255,255,.12)" : "none",
            background:
              variant === "icon"
                ? "linear-gradient(135deg,rgba(74,57,44,.44),rgba(42,32,25,.24))"
                : active
                  ? "linear-gradient(135deg,#FF8A2B,#FF5900)"
                  : "transparent",
            color: variant === "icon" ? (active ? "#FF8A4B" : "#C0B2A4") : "#F3ECE3",
            cursor: "pointer",
            fontSize: variant === "menu" ? 13.5 : undefined,
          }}
        >
          <Bell className="h-[18px] w-[18px]" />
          {variant === "menu" && <span>{t("notifications.title")}</span>}
          {count > 0 && (
            <span
              className="absolute flex items-center justify-center text-[10px] font-bold text-white"
              style={{
                top: count > 9 ? 4 : 7,
                right: count > 9 ? 4 : 11,
                minWidth: count > 9 ? 16 : 7,
                height: count > 9 ? 16 : 7,
                padding: count > 9 ? "0 4px" : 0,
                borderRadius: 999,
                background: "#FF5900",
                boxShadow: "0 0 8px rgba(255,89,0,.7)",
              }}
            >
              {count > 9 ? (count > 99 ? "99+" : count) : ""}
            </span>
          )}
      </a>
    </Link>
  );
}
